// Local-mode mocks for the Applications product's sign-ins. Development only.
//
// /ad/GetAuthLog with protocol "oidc", "saml" or "mfa_api" (the Applications
// dashboard and the Sign-in logs screen in Applications mode) gets the rows
// below, written the way authnull-service internal/idp/audit.go writes them:
// gatewayId "idp:<appId>", realm = app name, serviceSpn = client ID (OIDC,
// MFA API) or SAML entity ID, decision allow | deny, reason the server's code
// ("fed+mfa", "push_approved", "mfa_denied", "not_assigned"…; for MFA API
// apps the policy codes: push_approved, no_policy, policy_block…). The apps are
// the seeded ones in idpMocks.js (same ids). Filters honoured: sinceUnixMs,
// untilUnixMs, decision, excludeIntermediate, principal, gatewayId, page,
// pageSize (max 100); the timeline buckets by bucketMs like the server
// ((ts / bucketMs) * bucketMs, empty buckets left out).
//
// Any other query is handed on to RADIUS_LOGS_MOCKS (which in turn hands
// non-RADIUS queries to the Auth Logs mock), so order these before it.

import { RADIUS_LOGS_MOCKS } from "./radiusLogsMocks";

const NOW = Date.now();
const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

const APP_PROTOCOLS = new Set(["oidc", "saml", "mfa_api"]);

const APPS = {
  oidc: { id: "82fb7e5f-1c4d-4f7a-9d3e-5b2a6c8e0f11", name: "IdP Test App", spn: "c2fb8383-6a1e-4b0f-8d2c-3e4f5a6b7c8d" },
  mfa_api: { id: "4a7c2e90-8b3d-4e1f-a6c5-0d9e8f7a6b5c", name: "AMSI ERP (MFA API test)", spn: "9e1d4c7b-2a5f-4d8e-b3c6-7f0a1e2d3c4b" },
  saml: { id: "c3d9a1e7-5f2b-4c8d-9e0a-1b2c3d4e5f60", name: "HR portal", spn: "https://hr.example.com/saml/metadata" },
};

const USERS = ["satyam.entra", "priya.k", "ops.bot", "r.mehta", "a.fernandes"];
const IPS = ["203.0.113.40", "198.51.100.23", "192.0.2.17", "203.0.113.88"];

// [decision, reason, weight] per protocol, using the server's reason codes.
const OUTCOMES = {
  oidc: [
    ["allow", "fed+mfa", 14],
    ["deny", "mfa_denied", 2],
    ["deny", "mfa_expired", 1],
    ["deny", "not_assigned", 1],
    ["deny", "not_in_directory", 1],
  ],
  // MFA API apps are decided by app policies (appReasons.js has the labels).
  mfa_api: [
    ["allow", "push_approved", 12],
    ["allow", "policy_allow", 2],
    ["deny", "push_denied", 2],
    ["deny", "push_expired", 1],
    ["deny", "no_policy", 2],
    ["deny", "policy_block", 1],
    ["deny", "not_in_directory", 1],
    ["deny", "rate_limited", 1],
  ],
  // No one is assigned to HR portal in idpMocks, so most attempts are refused.
  saml: [
    ["deny", "not_assigned", 6],
    ["allow", "fed+mfa", 1],
  ],
};

// Sign-ins per protocol over the last 7 days, half of them in the last 24 h.
const COUNTS = { oidc: 96, mfa_api: 74, saml: 22 };

// Deterministic pseudo-random numbers, so a reload shows the same picture.
function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const pickWeighted = (rand, list) => {
  const total = list.reduce((a, o) => a + o[2], 0);
  let r = rand() * total;
  for (const o of list) {
    r -= o[2];
    if (r < 0) return o;
  }
  return list[list.length - 1];
};

function makeEvents() {
  const rand = rng(20261026);
  const out = [];
  Object.entries(COUNTS).forEach(([protocol, n]) => {
    const app = APPS[protocol];
    for (let i = 0; i < n; i += 1) {
      const recent = i % 2 === 0;
      const age = recent ? rand() * DAY : DAY + rand() * 6 * DAY;
      const [decision, reason] = pickWeighted(rand, OUTCOMES[protocol]);
      const pushy = /^(push|mfa_denied|mfa_expired)|mfa$/.test(reason);
      out.push({
        gatewayId: `idp:${app.id}`,
        tenantId: 1,
        orgId: 1,
        correlationId: `idp-${protocol}-${i}-7c1e2f3a-4b5c-4d6e-8f70-81a2b3c4d5e6`,
        protocol,
        principal: USERS[Math.floor(rand() * USERS.length)],
        realm: app.name,
        serviceSpn: app.spn,
        clientIp: IPS[Math.floor(rand() * IPS.length)],
        decision,
        reason,
        upstreamErrorCode: 0,
        challengeId: pushy ? `ch-${protocol}-${i}` : "",
        tsUnixMs: Math.round(NOW - age),
      });
    }
  });
  return out.sort((a, b) => b.tsUnixMs - a.tsUnixMs);
}

const EVENTS = makeEvents();

function filterApps(body = {}) {
  const principal = String(body.principal || "").toLowerCase();
  const protocol = String(body.protocol || "").toLowerCase();
  return EVENTS.filter(
    (e) =>
      e.protocol === protocol &&
      (!body.decision || e.decision === body.decision) &&
      (!body.excludeIntermediate || e.decision !== "mfa_required") &&
      (!principal || e.principal.toLowerCase().includes(principal)) &&
      (!body.gatewayId || e.gatewayId === body.gatewayId) &&
      (!body.sinceUnixMs || e.tsUnixMs >= body.sinceUnixMs) &&
      (!body.untilUnixMs || e.tsUnixMs <= body.untilUnixMs),
  );
}

function timeline(events, bucketMs) {
  const bucket = Number(bucketMs) > 0 ? Number(bucketMs) : 15 * MIN;
  const counts = new Map();
  events.forEach((e) => {
    const t = Math.floor(e.tsUnixMs / bucket) * bucket;
    counts.set(t, (counts.get(t) || 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => a[0] - b[0]).map(([tsUnixMs, count]) => ({ tsUnixMs, count }));
}

const isAppQuery = (body) => APP_PROTOCOLS.has(String(body?.protocol || "").toLowerCase()) && !body?.mode;

const nextAuthLogMock = RADIUS_LOGS_MOCKS.find((m) => m.pattern === "/ad/getauthlog");

export const APP_LOGS_MOCKS = [
  {
    pattern: "/ad/getauthlog",
    respond: (body) => {
      if (!isAppQuery(body)) {
        return nextAuthLogMock ? nextAuthLogMock.respond(body) : { code: 200, status: "Success", total: 0, events: [], timeline: [] };
      }
      const all = filterApps(body || {});
      const page = Math.max(1, Number(body?.page) || 1);
      const raw = Number(body?.pageSize) || 0;
      const pageSize = raw <= 0 || raw > 100 ? 25 : raw;
      return {
        code: 200,
        status: "Success",
        message: "ok",
        total: all.length,
        page,
        pageSize,
        events: all.slice((page - 1) * pageSize, page * pageSize),
        timeline: timeline(all, body?.bucketMs),
      };
    },
  },
];
